import { formatAppDate, formatAppDateTime, formatAppTime } from "./appTimeFormat";
import { formatTransactionDescription } from "./paymentHelpers";
import { isProductEnabled } from "../config/products";

export const dashGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

export const portfolioValue = (cash = 0, coin = 0) =>
  (Number(cash) || 0) + (Number(coin) || 0) / 10;

/** Live session user wins over dashboard profile (profile can lag after socket/game updates). */
export const resolveWalletBalances = (profile, authUser) => ({
  cash: Number(authUser?.CASH ?? profile?.CASH ?? 0),
  coin: Number(authUser?.COIN ?? profile?.COIN ?? 0),
});

/** Cash-equivalent wallet mix for portfolio bar + legend. */
export const walletMixShares = (cash = 0, coin = 0, coinRate = 10) => {
  const coinAsCash = (Number(coin) || 0) / coinRate;
  const portfolio = (Number(cash) || 0) + coinAsCash;

  if (portfolio <= 0) {
    return { cashShare: 0, coinShare: 0, coinBarShare: 0, cashBarShare: 100 };
  }

  const coinShare = (coinAsCash / portfolio) * 100;
  const cashShare = 100 - coinShare;

  return {
    cashShare,
    coinShare,
    coinBarShare: coinShare,
    cashBarShare: cashShare,
  };
};

const mixShareDecimals = (smallerPct) => {
  if (smallerPct >= 10) return 1;
  if (smallerPct >= 1) return 2;
  if (smallerPct >= 0.1) return 3;
  if (smallerPct >= 0.01) return 4;
  return 5;
};

const roundMixPct = (n, decimals) => {
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
};

const trimMixPct = (n, decimals) => {
  if (n <= 0) return "0";
  if (n >= 100) return "100";
  return n.toFixed(decimals).replace(/\.?0+$/, "") || "0";
};

/**
 * Format cash + coin mix labels that always sum to 100%.
 * Precision follows the smaller slice so tiny coin holdings stay visible.
 */
export const formatMixSharePair = (cashShare, coinShare) => {
  const coin = Math.max(0, Math.min(100, Number(coinShare) || 0));
  const cash = Math.max(0, Math.min(100, Number(cashShare) || 0));

  if (coin <= 0) return { cash: "100", coin: "0" };
  if (cash <= 0) return { cash: "0", coin: "100" };

  const decimals = mixShareDecimals(Math.min(cash, coin));
  const coinRounded = roundMixPct(coin, decimals);
  const cashRounded = roundMixPct(100 - coinRounded, decimals);

  return {
    cash: trimMixPct(cashRounded, decimals),
    coin: trimMixPct(coinRounded, decimals),
  };
};

export const SCORE_CATEGORIES = [
  { key: "houseScores", label: "Bet House" },
  { key: "bettingsMadeScores", label: "Bettings Made" },
  { key: "predictionsScores", label: "Predictions" },
  { key: "p2pScores", label: "P2P Trades" },
  { key: "casinoScore", label: "Casino" },
  { key: "bondDrawScore", label: "Bond Draw" },
  { key: "polymarketScore", label: "Polymarket" },
];

export const computeTotalScore = (row = {}) =>
  SCORE_CATEGORIES.reduce((sum, { key }) => sum + (Number(row[key]) || 0), 0);

export const formatBalance = (value, { compact = false, full = false } = {}) => {
  const n = Number(value) || 0;
  if (full) return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (compact) {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  }
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const TX_TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "deposit", label: "Deposits" },
  { value: "withdrawal", label: "Withdrawals" },
  { value: "p2p", label: "P2P" },
  { value: "predictions", label: "Predictions" },
  { value: "wins", label: "Wins" },
  { value: "losses", label: "Losses" },
  { value: "cancels", label: "Cancels" },
  { value: "fees", label: "Fees" },
  { value: "rewards", label: "Rewards" },
  { value: "conversion", label: "Conversion" },
];

export const TX_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "success", label: "Success" },
  { value: "pending", label: "Pending" },
  { value: "hold", label: "On hold" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

export const TX_PER_DAY_PAGE = 5;

export const txStatusLabel = (status = "") => {
  const s = String(status).toLowerCase();
  if (s === "hold" || s === "onhold") return "On hold";
  if (!status) return "Pending";
  return String(status);
};

export const txCurrencyLabel = (currency = "") => {
  const c = String(currency).toUpperCase();
  if (c === "COIN") return "Coin";
  if (c === "CASH" || c === "RWF") return "Cash";
  return currency || "Cash";
};

export const fmtTxMoney = (value, currency = "CASH") => {
  const n = Number(value) || 0;
  const formatted = n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return `${txCurrencyLabel(currency)} ${formatted}`;
};

const CREDIT_TYPES = new Set([
  "deposit",
  "bonuscredit",
  "leaderboardbonus",
  "adminreward",
  "commission",
  "referralreward",
  "firstdepositreward",
  "winbet",
  "refund",
  "reversal",
]);

const DEBIT_TYPES = new Set([
  "withdrawal",
  "platformfee",
  "commissionfee",
  "losebet",
]);

/** Category + icon key for ledger visuals (mirrors mobile TransactionSection). */
export const getTxCategory = (typeRaw = "") => {
  const type = String(typeRaw).toLowerCase();

  if (["deposit", "bonuscredit", "leaderboardbonus", "adminreward", "commission", "referralreward", "firstdepositreward"].includes(type)) {
    return { family: "credit", icon: "plus-circle" };
  }
  if (["withdrawal", "platformfee", "commissionfee"].includes(type)) {
    return { family: "debit", icon: "minus-circle" };
  }
  if (["betting", "createp2p"].includes(type)) {
    return { family: "betting", icon: "play-circle" };
  }
  if (["winbet"].includes(type)) {
    return { family: "win", icon: "trophy" };
  }
  if (["losebet"].includes(type)) {
    return { family: "loss", icon: "x-circle" };
  }
  if (["cancelbet", "reversal", "refund"].includes(type)) {
    return { family: "cancel", icon: "ban" };
  }
  if (["transfer", "conversion"].includes(type)) {
    return { family: "transfer", icon: "swap" };
  }
  if (type === "tip") {
    return { family: "tip", icon: "heart" };
  }
  return { family: "default", icon: "file" };
};

export const txAmountParts = (tx = {}) => {
  const status = String(tx.status || "").toLowerCase();
  const failed = ["failed", "cancelled", "canceled"].includes(status);
  const pending = ["pending", "hold", "onhold"].includes(status);
  const amount = Number(tx.amount) || 0;
  const { family } = getTxCategory(tx.transactionType);
  const isCredit = ["credit", "win"].includes(family);
  const isDebit = ["debit", "loss", "fees"].includes(family);

  if (failed || pending) {
    return {
      prefix: "",
      amount,
      signed: 0,
      neutral: true,
      tone: failed ? "danger" : "muted",
    };
  }

  if (isCredit) {
    return { prefix: "+", amount, signed: amount, neutral: false, tone: "credit" };
  }
  if (isDebit) {
    return { prefix: "-", amount, signed: -amount, neutral: false, tone: "debit" };
  }

  const signed = txSignedAmountFromBalance(tx);
  return {
    prefix: signed >= 0 ? "+" : "",
    amount: Math.abs(amount),
    signed,
    neutral: false,
    tone: signed >= 0 ? "credit" : "debit",
  };
};

const txSignedAmountFromBalance = (tx = {}) => {
  if (typeof tx.balanceAfter === "number" && typeof tx.balanceBefore === "number") {
    return tx.balanceAfter - tx.balanceBefore;
  }
  const type = String(tx.transactionType || "").toLowerCase();
  const amount = Number(tx.amount) || 0;
  if (CREDIT_TYPES.has(type)) return amount;
  if (DEBIT_TYPES.has(type)) return -amount;
  return amount;
};

export const summarizeTxLedger = (transactions = []) => {
  let credits = 0;
  let debits = 0;
  let pending = 0;

  transactions.forEach((tx) => {
    const status = String(tx.status || "").toLowerCase();
    if (["pending", "hold", "onhold"].includes(status)) {
      pending += 1;
      return;
    }
    if (["failed", "cancelled", "canceled"].includes(status)) return;

    const { signed, neutral } = txAmountParts(tx);
    if (neutral) return;
    if (signed > 0) credits += signed;
    else if (signed < 0) debits += Math.abs(signed);
  });

  return { count: transactions.length, credits, debits, pending };
};

export const buildTxDetailRows = (tx = {}) => {
  const gross = Number(tx.amount) || 0;
  const platformFee = Number(tx.platformFee) || 0;
  const otherFees = Number(tx.otherFees) || 0;
  const net = Math.max(0, gross - platformFee - otherFees);
  const hasLoan = Number(tx.loanAmountBorrowed) > 0;

  const rows = [
    { label: "Currency", value: txCurrencyLabel(tx.currency) },
    { label: "Status", value: txStatusLabel(tx.status) },
    { label: "Total amount", value: fmtTxMoney(gross, tx.currency), highlight: hasLoan },
  ];

  if (platformFee || otherFees) {
    rows.push({ label: "Platform fee", value: fmtTxMoney(platformFee, tx.currency) });
    rows.push({ label: "Other fees", value: fmtTxMoney(otherFees, tx.currency) });
  }

  if (hasLoan) {
    rows.push({
      label: "Your cash payment",
      value: tx.cashAmount != null ? fmtTxMoney(tx.cashAmount, "CASH") : "—",
      highlight: true,
    });
  } else {
    rows.push({ label: "Amount (net)", value: fmtTxMoney(net, tx.currency) });
  }

  if (tx.balanceBefore != null) {
    rows.push({ label: "Balance before", value: fmtTxMoney(tx.balanceBefore, tx.currency) });
  }
  if (tx.balanceAfter != null) {
    rows.push({ label: "Balance after", value: fmtTxMoney(tx.balanceAfter, tx.currency) });
  }

  if (hasLoan) {
    rows.push({ label: "Loan amount", value: fmtTxMoney(tx.loanAmountBorrowed, "CASH"), highlight: true });
    rows.push({ label: "Interest rate", value: `${Number(tx.loanInterestRate) || 0}%` });
    rows.push({
      label: "Total to repay",
      value: fmtTxMoney(
        (Number(tx.loanAmountBorrowed) || 0) + (Number(tx.loanInterestBalance) || 0),
        "CASH"
      ),
      highlight: true,
    });
  }

  if (tx.userPaymentMethod) rows.push({ label: "Payment method", value: tx.userPaymentMethod });
  if (tx.method) rows.push({ label: "Method", value: tx.method });
  if (tx.phoneNumber) rows.push({ label: "Phone number", value: tx.phoneNumber, copyable: true });
  if (tx.chosenOptionNumber) {
    rows.push({ label: "Withdrawal number", value: tx.chosenOptionNumber, copyable: true });
  }
  if (tx.adminPhoneNumber) rows.push({ label: "Admin contact", value: tx.adminPhoneNumber, copyable: true });
  if (tx.holdReason) {
    rows.push({
      label: "Hold reason",
      value: formatTransactionDescription(tx.holdReason),
      alert: true,
    });
  }
  else if (tx.description) {
    rows.push({
      label: "Description",
      value: formatTransactionDescription(tx.description),
    });
  }
  if (tx.transactionRef) rows.push({ label: "Reference", value: tx.transactionRef, copyable: true });
  if (tx.createdAt) {
    rows.push({ label: "Date", value: formatAppDateTime(tx.createdAt) });
  }

  return rows;
};

export const getTxDateKey = (tx) => {
  if (!tx?.createdAt) return "unknown";
  const d = new Date(tx.createdAt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const txStatusTone = (status = "") => {
  const s = String(status).toLowerCase();
  if (["success", "confirmed"].includes(s)) return "success";
  if (["pending", "hold", "onhold"].includes(s)) return "muted";
  if (["failed", "cancelled", "canceled"].includes(s)) return "danger";
  return "primary";
};

export const txTypeLabel = (type = "") => {
  const typeLabels = {
    betting: "P2P stake",
    createp2p: "Create P2P",
    winbet: "P2P win",
    losebet: "P2P loss",
    cancelbet: "P2P cancel",
    platformfee: "Prediction fee",
    commission: "Prediction commission",
    commissionfee: "Prediction commission fee",
  };
  const key = String(type).toLowerCase();
  if (typeLabels[key]) return typeLabels[key];
  return String(type)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .trim()
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
};

export const txSignedAmount = (tx = {}) => txAmountParts(tx).signed;

export const formatTxDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return formatAppDate(d);
};

export const formatTxTime = (value) => {
  if (!value) return "";
  return formatAppTime(value);
};

const RAW_DASH_NAV_GROUPS = [
  {
    id: "desk",
    label: "Dashboard",
    items: [
      { type: "section", id: "workspace", label: "Workspace", hint: "Analytics & activity" },
      { type: "section", id: "transactions", label: "Transactions", hint: "Full ledger" },
      { type: "section", id: "leaderboard", label: "Leaderboard", hint: "Scores & ranks" },
    ],
  },
  {
    id: "play",
    label: "Markets & play",
    items: [
      { type: "link", id: "markets", label: "Bet Markets", to: "/app/bethouse/manage", hint: "Markets you run", product: "house" },
      { type: "link", id: "joined", label: "Joined Bets", to: "/app/joined-bets", hint: "Bets you entered", product: "house" },
      { type: "link", id: "picks", label: "My Picks", to: "/app/predictions", hint: "Prediction history", product: "tips" },
    ],
  },
  {
    id: "account",
    label: "Account",
    items: [
      { type: "link", id: "requests", label: "Requests", to: "/app/requests", hint: "P2P invites" },
      { type: "link", id: "referrals", label: "Referrals", to: "/app/referrals", hint: "Invite & earn" },
    ],
  },
];

const filterDashItems = (items) =>
  items.filter((item) => isProductEnabled(item.product));

export const DASH_NAV_GROUPS = RAW_DASH_NAV_GROUPS.map((group) => ({
  ...group,
  items: filterDashItems(group.items),
})).filter((group) => group.items.length);

/** @deprecated Use DASH_NAV_GROUPS */
export const DASH_NAV_ITEMS = DASH_NAV_GROUPS.flatMap((group) => group.items);

/** @deprecated Use DASH_NAV_ITEMS link entries instead */
export const MANAGE_TILES = [
  {
    id: "markets",
    eyebrow: "Created",
    title: "Bet Markets",
    description: "Manage the markets you launched",
    to: "/app/bethouse/manage",
    product: "house",
    comingSoon: false,
  },
  {
    id: "joined",
    eyebrow: "Entered",
    title: "Joined Bets",
    description: "Track bets you've entered",
    to: "/app/joined-bets",
    product: "house",
    comingSoon: false,
  },
  {
    id: "requests",
    eyebrow: "Inbox",
    title: "Requests",
    description: "P2P invites you've sent and received",
    to: "/app/requests",
  },
  {
    id: "picks",
    eyebrow: "Predictions",
    title: "My Picks",
    description: "Review your prediction history",
    to: "/app/predictions",
    product: "tips",
    comingSoon: false,
  },
].filter((tile) => isProductEnabled(tile.product));
